import { Phone, MapPin, ArrowUpRight, ArrowRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import { scrollToId } from "@/lib/scroll";

export default function Footer() {
  return (
    <footer className="relative bg-forest-deep">
      <div className="mx-auto max-w-7xl px-5 pt-24 sm:px-8 sm:pt-32">
        <FadeIn>
          <div className="relative overflow-hidden rounded-3xl bg-forest p-10 sm:p-16">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-400/15 blur-[80px]" />
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300 sm:text-xs">
              Et maintenant ?
            </p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Prêt à dire adieu à votre vieille chaudière ?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">
              Simulez vos aides en 2 minutes et soyez rappelé sous 24 h
              ouvrées par un conseiller Ener Solutions.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="footer-cta-simulator"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("#simulateur");
                }}
                className="group flex items-center gap-2 rounded-full bg-emerald-400 px-7 py-4 text-base font-bold text-forest-deep transition-all hover:scale-[1.03] hover:bg-emerald-300"
              >
                Tester mon éligibilité
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href="tel:+33535759803"
                data-testid="footer-cta-phone"
                className="flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-base font-semibold text-white transition-all hover:border-emerald-300 hover:text-emerald-300"
              >
                <Phone className="h-4 w-4" /> Appeler le 05 35 75 98 03
              </a>
            </div>
          </div>
        </FadeIn>

        <div className="grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <img
                src="/logo-enersolutions.png"
                alt="Ener Solutions"
                className="h-10 w-auto rounded-md bg-white/95 px-2 py-1.5"
              />
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/50">
              Installation de pompes à chaleur air/eau, panneaux solaires et
              climatisation. Démarches d'aides 100 % gérées.
            </p>
          </div>

          <div>
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300">
              Contact
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-white/60">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400/70" />
                3 rue de la Dauge, 33240 Saint-André-de-Cubzac
              </li>
              <li>
                <a
                  href="tel:+33535759803"
                  data-testid="footer-phone"
                  className="flex items-center gap-2.5 transition-colors hover:text-emerald-300"
                >
                  <Phone className="h-4 w-4 text-emerald-400/70" /> 05 35 75
                  98 03
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300">
              Zones d'intervention
            </h3>
            <ul className="mt-5 space-y-2.5 text-sm text-white/60">
              <li>Gironde</li>
              <li>Charente</li>
              <li>Charente-Maritime</li>
              <li>Dordogne</li>
              <li>Lot-et-Garonne</li>
              <li>Landes</li>
            </ul>
            <a
              href="/climatisation"
              data-testid="footer-link-climatisation"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-sky-400/40 px-3.5 py-1.5 text-xs font-semibold text-sky-300 transition-colors hover:bg-sky-400/10"
            >
              Climatisation Daikin <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div>
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300">
              Sur cette page
            </h3>
            <ul className="mt-5 space-y-2.5 text-sm text-white/60">
              {[
                ["Les aides", "#aides"],
                ["Le simulateur", "#simulateur"],
                ["Notre méthode", "#methode"],
                ["FAQ", "#faq"],
              ].map(([label, href]) => (
                <li key={href}>
                  <a
                    href={href}
                    data-testid={`footer-link-${href.slice(1)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(href);
                    }}
                    className="transition-colors hover:text-emerald-300"
                  >
                    {label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="https://www.ener-solutions.pro"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-website"
                  className="flex items-center gap-1.5 transition-colors hover:text-emerald-300"
                >
                  www.ener-solutions.pro{" "}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 py-8 text-[11px] leading-relaxed text-white/35 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} Ener Solutions — Installateur RGE
            QualiPAC & QualiPV.
          </p>
          <p className="max-w-xl sm:text-right">
            *Estimations indicatives selon les barèmes 2026, sous conditions de
            ressources et d'éligibilité du logement.
          </p>
        </div>
      </div>
    </footer>
  );
}
